import { CommonModule } from '@angular/common';
import {
  Component,
  OnInit,
  OnDestroy,
  ElementRef,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

import { Subscription } from 'rxjs';

import { AlertConfig, AlertQueueItem } from '@shared-models';

import { AlertService } from '../../services/alert/alert.service';

/**
 * Modern Alert Component following Angular 19 best practices and BEM methodology
 * Displays modal alerts with various types and actions
 */
@Component({
  selector: 'v-one-alert',
  imports: [CommonModule],
  templateUrl: './v-one-alert.component.html',
  styleUrls: ['./v-one-alert.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VOneAlertComponent implements OnInit, OnDestroy {
  // Modern Angular 19 dependency injection
  private readonly alertService = inject(AlertService);
  private readonly elementRef = inject(ElementRef);
  private readonly sanitizer = inject(DomSanitizer);

  // Reactive state using signals
  readonly config = signal<AlertConfig | null>(null);
  readonly safeIconSvg = signal<SafeHtml | null>(null);

  private subscription: Subscription | null = null;
  private previouslyFocusedElement: HTMLElement | null = null;

  // Touch gesture support for mobile
  private touchStartY = 0;
  private touchStartX = 0;
  private isDragging = false;

  ngOnInit(): void {
    this.subscription = this.alertService.alert$.subscribe((queueItem) => {
      this.handleAlertChange(queueItem);
    });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  onAccept(): void {
    this.triggerHapticFeedback('success');
    this.alertService.resolve('accept');
    this.restoreFocus();
  }

  onReject(): void {
    this.triggerHapticFeedback('warning');
    this.alertService.resolve('reject');
    this.restoreFocus();
  }

  onClose(): void {
    this.triggerHapticFeedback('light');
    this.alertService.resolve('close');
    this.restoreFocus();
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.onClose();
    }
  }

  /**
   * Touch gesture handlers for mobile swipe-to-dismiss
   */
  onTouchStart(event: TouchEvent): void {
    if (event.touches.length === 1) {
      this.touchStartY = event.touches[0].clientY;
      this.touchStartX = event.touches[0].clientX;
      this.isDragging = false;
    }
  }

  onTouchMove(event: TouchEvent): void {
    if (event.touches.length === 1 && !this.isDragging) {
      const touchY = event.touches[0].clientY;
      const touchX = event.touches[0].clientX;
      const deltaY = Math.abs(touchY - this.touchStartY);
      const deltaX = Math.abs(touchX - this.touchStartX);

      // Detect vertical swipe (swipe up or down to dismiss)
      if (deltaY > 50 && deltaY > deltaX) {
        this.isDragging = true;
        this.onClose();
      }
    }
  }

  onTouchEnd(): void {
    this.isDragging = false;
    this.touchStartY = 0;
    this.touchStartX = 0;
  }

  /**
   * Gets BEM-based CSS classes for the alert dialog
   */
  getAlertClasses(): string {
    const currentConfig = this.config();
    if (!currentConfig) return '';

    const type = currentConfig.type || 'info';
    const classes = ['v-one-alert__dialog', `v-one-alert__dialog--${type}`];

    if (currentConfig.showIcon !== false) {
      classes.push('v-one-alert__dialog--with-icon');
    }

    if (currentConfig.showClose) {
      classes.push('v-one-alert__dialog--with-close');
    }

    return classes.join(' ');
  }

  /**
   * Gets BEM-based CSS classes for action buttons
   */
  getButtonClasses(buttonType: 'accept' | 'reject'): string {
    const currentConfig = this.config();
    if (!currentConfig) return '';

    const type = currentConfig.type || 'info';
    const classes = [
      'v-one-alert__button',
      `v-one-alert__button--${buttonType}`,
      `v-one-alert__button--${type}`,
    ];

    return classes.join(' ');
  }

  /**
   * Handles alert queue item changes with mobile optimizations
   */
  private handleAlertChange(queueItem: AlertQueueItem | null): void {
    if (queueItem === null) {
      this.config.set(null);
      this.safeIconSvg.set(null);
      return;
    }

    this.config.set(queueItem.config);
    this.safeIconSvg.set(this.createSafeIconSvg(queueItem.config.type));
    this.saveFocus();

    // Use requestAnimationFrame for better performance and mobile smoothness
    requestAnimationFrame(() => {
      this.focusDialog();

      // Prevent background scroll on mobile when alert is open
      if (this.isMobileDevice()) {
        document.body.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.width = '100%';
      }
    });
  }

  /**
   * Saves the currently focused element for restoration later
   */
  private saveFocus(): void {
    this.previouslyFocusedElement = document.activeElement as HTMLElement;
  }

  /**
   * Restores focus to the previously focused element and resets mobile scroll lock
   */
  private restoreFocus(): void {
    // Restore background scroll on mobile
    if (this.isMobileDevice()) {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    }

    if (this.previouslyFocusedElement) {
      this.previouslyFocusedElement.focus();
      this.previouslyFocusedElement = null;
    }
  }

  /**
   * Focuses the alert dialog for accessibility
   */
  private focusDialog(): void {
    const dialog = this.elementRef.nativeElement.querySelector(
      '.v-one-alert__dialog',
    );
    if (dialog) {
      dialog.focus();
    }
  }

  /**
   * Creates sanitized SVG icons based on alert type
   */
  private createSafeIconSvg(type?: string): SafeHtml | null {
    const svg = this.getIconSvg(type);
    return svg ? this.sanitizer.bypassSecurityTrustHtml(svg) : null;
  }

  /**
   * Detects if the current device is mobile for optimization purposes
   */
  private isMobileDevice(): boolean {
    return (
      window.innerWidth <= 768 ||
      'ontouchstart' in window ||
      navigator.maxTouchPoints > 0
    );
  }

  /**
   * Triggers haptic feedback on supported mobile devices
   */
  private triggerHapticFeedback(
    type: 'light' | 'medium' | 'heavy' | 'success' | 'warning' | 'error',
  ): void {
    if (this.isMobileDevice() && 'vibrate' in navigator) {
      const patterns = {
        light: [10],
        medium: [25],
        heavy: [50],
        success: [10, 50, 10],
        warning: [25, 25, 25],
        error: [50, 25, 50, 25, 50],
      };

      navigator.vibrate(patterns[type] || patterns.light);
    }
  }

  /**
   * Returns SVG markup for alert type icons using predefined colors
   */
  private getIconSvg(type?: string): string | null {
    switch (type) {
      case 'success':
        return `<svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="16" cy="16" r="16" fill="var(--color-success-600)"/>
          <path d="M10 17.5L14 21.5L22 13.5" stroke="var(--color-white)" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>`;
      case 'error':
        return `<svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="16" cy="16" r="16" fill="var(--color-orange-500)"/>
          <path d="M20 12L12 20" stroke="var(--color-white)" stroke-width="2.5" stroke-linecap="round"/>
          <path d="M12 12L20 20" stroke="var(--color-white)" stroke-width="2.5" stroke-linecap="round"/>
        </svg>`;
      case 'warning':
        return `<svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="16" cy="16" r="16" fill="var(--color-warning-600)"/>
          <path d="M16 10V18" stroke="var(--color-gray-700)" stroke-width="2.5" stroke-linecap="round"/>
          <circle cx="16" cy="22" r="1.5" fill="var(--color-gray-700)"/>
        </svg>`;
      case 'info':
        return `<svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="16" cy="16" r="16" fill="var(--color-blue-500)"/>
          <circle cx="16" cy="10" r="1.5" fill="var(--color-white)"/>
          <path d="M16 14V22" stroke="var(--color-white)" stroke-width="2.5" stroke-linecap="round"/>
        </svg>`;
      default:
        return null;
    }
  }
}
